import { CHANNELS } from "../../shared/channels";
import { ACHIEVEMENTS, AWARD_CATEGORIES, Achievement, AwardCategory, AwardsSummary, BoardEntry } from "../../shared/achievements";
import { call } from "../bridge";
import { byId } from "../dom";
import { stampIcons } from "../icons";
import { locale, t } from "../i18n";
import { awardsNow, boardNow, loadAwards, onAwards, remember } from "../awards_store";
import { miniMedal, paintCase } from "../medal_case";
import { isViewActive, onViewShown } from "../navigation";

// the achievements view: every ladder with its progress, the medal case with its workbench, and the leaderboard.
// the home modules for the leaderboard and the medals use the same pieces

type Tab = "list" | "medals" | "ranks";

let tab: Tab = "list";
let category: AwardCategory | "all" = "all";
const open = new Set<string>();

const number = (value: number): string => value.toLocaleString(locale());

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = "", text = ""): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function ladders(): Map<string, Achievement[]> {
  const groups = new Map<string, Achievement[]>();
  for (const item of ACHIEVEMENTS) groups.set(item.counter, [...(groups.get(item.counter) || []), item]);
  return groups;
}

function ladderRow(steps: Achievement[], states: Map<string, { progress: number; done: boolean }>): HTMLElement {
  const finished = steps.filter((step) => states.get(step.id)?.done).length;
  const next = steps.find((step) => !states.get(step.id)?.done) || steps[steps.length - 1];
  const progress = states.get(next.id)?.progress || 0;
  const secret = next.secret && finished === 0;
  const row = el("div", `ladder${finished === steps.length ? " complete" : ""}`);
  const head = el("button", "ladder-head");
  head.type = "button";
  head.setAttribute("aria-expanded", String(open.has(next.counter)));
  const badge = el("span", "ladder-icon");
  badge.innerHTML = `<i data-icon="${secret ? "lock" : finished === steps.length ? "check" : next.icon}" data-size="20"></i>`;
  const body = el("span", "ladder-body");
  body.append(el("span", "ladder-name", secret ? "?" : t(next.name)), el("span", "ladder-desc", secret ? t("keep going to find out") : next.desc));
  const bar = el("span", "ladder-bar");
  const fill = el("i");
  fill.style.transform = `scaleX(${finished === steps.length ? 1 : Math.min(1, progress / next.target)})`;
  bar.append(fill);
  body.append(bar);
  const meta = el("span", "ladder-meta");
  meta.append(el("b", "pts", `+${next.points}`), el("small", "", `${finished}/${steps.length}`));
  head.append(badge, body, meta);
  row.append(head);
  const list = el("ul", "ladder-steps");
  list.hidden = !open.has(next.counter);
  for (const step of steps) {
    const done = Boolean(states.get(step.id)?.done);
    const item = el("li", done ? "done" : "");
    item.append(el("span", "", done ? "✓" : "·"), el("span", "", step.secret && !done && finished === 0 ? "?" : t(step.name)), el("small", "", `${number(step.target)} · +${step.points}`));
    list.append(item);
  }
  row.append(list);
  head.addEventListener("click", () => {
    const now = list.hidden;
    list.hidden = !now;
    head.setAttribute("aria-expanded", String(now));
    if (now) open.add(next.counter);
    else open.delete(next.counter);
  });
  return row;
}

function paintList(summary: AwardsSummary): void {
  const states = new Map(summary.achievements.map((item) => [item.id, item]));
  const chips = byId("ach-cats");
  chips.replaceChildren(...[{ id: "all", label: "All" }, ...AWARD_CATEGORIES].map((item) => {
    const chip = el("button", `chip${category === item.id ? " active" : ""}`, t(item.label));
    chip.type = "button";
    chip.addEventListener("click", () => {
      category = item.id as AwardCategory | "all";
      paintList(summary);
    });
    return chip;
  }));
  const groups = [...ladders().values()].filter((steps) => category === "all" || steps[0].category === category);
  const sections = AWARD_CATEGORIES.filter((item) => category === "all" || item.id === category).map((item) => {
    const mine = groups.filter((steps) => steps[0].category === item.id);
    const box = el("section", "panel ach-cat");
    const total = summary.categories.find((entry) => entry.id === item.id);
    const head = el("div", "section-head");
    head.append(el("h2", "", t(item.label)), el("span", "hint", `${number(total?.points || 0)} / ${number(total?.max || 0)}`));
    box.append(head, ...mine.map((steps) => ladderRow(steps, states)));
    return box;
  });
  byId("ach-list").replaceChildren(...sections);
  stampIcons(byId("ach-list"));
}

function paintMedals(summary: AwardsSummary): void {
  paintCase(byId("ach-medals"), summary, paint);
}

export function rankRow(entry: BoardEntry, index: number): HTMLElement {
  const row = el("li", `rank${entry.you ? " me" : ""}`);
  const who = el("span", "rank-who");
  who.append(el("span", "rank-name", entry.you ? `${entry.name} · ${t("you")}` : entry.name));
  const medals = el("span", "rank-medals");
  for (const medal of entry.medals) {
    const mini = miniMedal(medal, 28);
    if (mini) medals.append(mini);
  }
  who.append(medals);
  // the six medals shown are the best ones, this says how the whole collection is kept
  if (entry.held) {
    const status = el("span", "rank-status");
    status.append(el("span", "", t("{n} medals", { n: entry.held })));
    if (entry.shining) status.append(el("span", "is-shining", t("{n} shining", { n: entry.shining })));
    if (entry.rusty) status.append(el("span", "is-rusty", t("{n} rusty", { n: entry.rusty })));
    who.append(status);
  }
  row.append(el("b", "rank-no", String(index + 1)), who, el("span", "rank-score", number(entry.score)));
  return row;
}

function paintRanks(): void {
  const board = boardNow();
  byId("ach-ranks").replaceChildren(...board.map(rankRow));
  const visible = byId<HTMLInputElement>("ach-visible");
  visible.checked = awardsNow()?.visible !== false;
}

function paint(): void {
  const summary = awardsNow();
  if (!summary) return;
  byId("ach-points").textContent = number(summary.points);
  byId("ach-shine").textContent = number(summary.shine);
  byId("ach-rank").textContent = summary.rank ? `#${summary.rank}` : "-";
  byId("ach-rank-label").textContent = summary.people > 1 ? t("of {n}", { n: summary.people }) : t("rank");
  byId("ach-sub").textContent = t("{done} of {total} achievements", { done: summary.unlocked, total: summary.total });
  byId("ach-pane-list").hidden = tab !== "list";
  byId("ach-pane-medals").hidden = tab !== "medals";
  byId("ach-pane-ranks").hidden = tab !== "ranks";
  byId("ach-tabs").querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((button) => button.classList.toggle("active", button.dataset.tab === tab));
  if (tab === "list") paintList(summary);
  if (tab === "medals") paintMedals(summary);
  if (tab === "ranks") paintRanks();
}

export function setupAchievementsView(): void {
  byId("ach-tabs").addEventListener("click", (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>("[data-tab]");
    if (!button) return;
    tab = button.dataset.tab as Tab;
    paint();
  });
  byId<HTMLInputElement>("ach-visible").addEventListener("change", (event) => {
    void call<AwardsSummary>(CHANNELS.awardsVisible, (event.target as HTMLInputElement).checked).then((next) => {
      remember(next);
      return loadAwards();
    });
  });
  onAwards(() => {
    if (isViewActive("achievements")) paint();
  });
  onViewShown("achievements", (detail) => {
    if (detail === "medals" || detail === "ranks" || detail === "list") tab = detail;
    void loadAwards().then(paint);
    paint();
  });
}

